import { GeneralFilterIcon, GeneralInstantBookIcon } from '@outdoorsyco/bonfire';
import xor from 'lodash/xor';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useMenuTriggerState } from 'react-stately';

import { useAmenitiesFilterList } from '@/constants/amenitiesFilter';
import { EFilterCategories, ESearchFilters } from '@/constants/searchFilters';
import { allVehicleStyles } from '@/constants/vehicleDetails';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useBringItToMeFilter } from '@/hooks/useBringItToMeFilter';
import { useReMountableElementRef } from '@/hooks/useReMountableElementRef';
import { useRouter } from '@/hooks/useRouter';
import { useSelectedCampground } from '@/hooks/useSelectedCampground';
import { applySearchFilter, setSelectedFilter } from '@/redux/modules/search';
import { EFilterMethod, setFormFilter } from '@/redux/modules/searchForm';
import { TRootState } from '@/redux/rootReducer';
import { setFilterVehiclesShow } from '@/redux/selectors/analytics-selectors';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { getIsStationaryFilterTriggered } from '@/redux/selectors/search/searchResults';
import { OptimizelyFlags, useExperimentIsEnabled } from '@/services/experiments';
import { TVehicleType } from '@/services/types/search/rentals/id';
import { getParamAsString, isDeliveryModeFromParams } from '@/utility/queryParams';
import {
  amenitiesFromFilterFeatureArray,
  moreFromFilterFeatureArray,
} from '@/utility/searchFilters';

import { useFilterPillsCtx } from '../FilterPillsContext/FilterPillsContext';
import FilterPills from './FilterPills';
import FiltersPillsContentContainer from './FilterPillsContentContainer';

const FilterPillsContainer: React.FC = () => {
  const intl = useIntl();
  const router = useRouter();
  const { isMobile } = useBreakpoint();
  const { isAutocompleteOpen, hideFilter, isCampgrounds } = useFilterPillsCtx();
  const dispatch = useDispatch();
  // Check if rental vs delivery search mode flag is enabled
  const isRentalsVDeliverySearchModeEnabled = useExperimentIsEnabled(
    OptimizelyFlags.RENTAL_V_DELIVERY_SEARCH_MODE,
  );

  const amenitiesItems = useAmenitiesFilterList(false, isRentalsVDeliverySearchModeEnabled);
  const queryParams = useSelector(getQueryParams);
  const formState = useSelector((state: TRootState) => state.searchForm.filters);
  const shouldMergeForm = useSelector((state: TRootState) => state.localitySearch.shouldMergeForm);
  const filtersFromQuery = useMemo(() => {
    // Start with query params and initial params
    const baseFilters = { ...queryParams };

    // IMPORTANT: Always check for cleared filters first, regardless of shouldMergeForm
    // This ensures filter pills show as inactive immediately after reset
    Object.entries(formState).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        delete baseFilters[key as ESearchFilters];
      }
    });

    // Only proceed with adding form state values if shouldMergeForm is true and we have form state
    if (Object.keys(formState).length > 0 && shouldMergeForm === true) {
      // Add any form state values that aren't already present on first render -
      // Now that locality pages have search results, some form fields come in NOT in query params
      Object.entries(formState).forEach(([key, value]) => {
        if (value !== undefined && !(key in baseFilters)) {
          baseFilters[key] = `${value}`;
        }
      });
    }

    return baseFilters;
  }, [queryParams, formState, shouldMergeForm]);
  const [modalIsOpened, setModalIsOpened] = useState(false);
  const [dropdownIsOpened, setIsDropdownOpened] = useState(false);
  const { ref: triggerRef, setElement: setFieldElement } = useReMountableElementRef<HTMLElement>();

  const { selectedCampground } = useSelectedCampground();

  const {
    [ESearchFilters.FILTER_TAGS_SLUG]: filterTagsSlugFromQuery,
    [ESearchFilters.INSTANT_BOOK]: instantBookFromQuery,
    [ESearchFilters.FILTER_FEATURE]: featuresFromQuery,
    [ESearchFilters.CAMPGROUND_FEATURES]: campgroundFeaturesFromQuery,
    [ESearchFilters.DELIVERY_STATIONARY]: deliveryStationaryFromQuery,
    [ESearchFilters.FILTER_OWNERTYPE]: filterOwnerTypeFromQuery,
  } = useSelector(getQueryParams);

  const isDeliveryMode = isDeliveryModeFromParams(queryParams);

  const modalTriggerState = useMenuTriggerState({
    isOpen: modalIsOpened,
    onOpenChange: open => {
      setModalIsOpened(open);
    },
  });

  const dropdownTriggerState = useMenuTriggerState({
    isOpen: dropdownIsOpened,
    onOpenChange: open => {
      // do not close if autocomplete is open
      if (!open && isAutocompleteOpen) {
        return;
      }
      setIsDropdownOpened(open);
    },
  });

  const isModalOpen = modalTriggerState.isOpen;
  const isDropdownOpen = dropdownTriggerState.isOpen;

  const selectedFilter = useSelector<TRootState, EFilterCategories | null>(
    state => state.search.selectedFilter,
  );
  const isStationaryFilterTriggered = useSelector(getIsStationaryFilterTriggered);

  const getRVTypeCountFromQuery = useCallback(() => {
    const vehicleCount =
      getParamAsString(filtersFromQuery[ESearchFilters.FILTER_TYPE])
        ?.split(',')
        .filter(item => allVehicleStyles.includes(item as TVehicleType)).length || 0;
    return vehicleCount;
  }, [filtersFromQuery]);

  const getRVProfileCountFromQuery = useCallback(() => {
    const subTypesCount =
      getParamAsString(filtersFromQuery[ESearchFilters.CAMPSITE_SUPPORTED_SUBTYPES])?.split(',')
        .length || 0;
    const lengthCount =
      getParamAsString(filtersFromQuery[ESearchFilters.CAMPSITE_SUPPORTED_LENGTH]) !== undefined
        ? 1
        : 0;
    const slideoutsCount =
      getParamAsString(filtersFromQuery[ESearchFilters.CAMPSITE_SUPPORTED_SLIDEOUTS])?.split(',')
        .length || 0;

    return (subTypesCount && 1) + lengthCount + (slideoutsCount && 1);
  }, [filtersFromQuery]);

  const getPriceCountFromQuery = useCallback(() => {
    const scorePriceRange =
      Number(filtersFromQuery[ESearchFilters.PRICE_MIN]) > 0 ||
      Number(filtersFromQuery[ESearchFilters.PRICE_MAX])
        ? 1
        : 0;

    return scorePriceRange;
  }, [filtersFromQuery]);

  const getPetIsActiveFromQuery = useCallback(() => {
    const featuresFromQueryCurrent = isCampgrounds
      ? filtersFromQuery[ESearchFilters.CAMPGROUND_FEATURES]
      : filtersFromQuery[ESearchFilters.FILTER_FEATURE];
    const currentFeatures = featuresFromQueryCurrent?.toString().split(',') || [];

    return isCampgrounds
      ? currentFeatures.some(el => el === 'allows_pets')
      : currentFeatures.some(el => el === 'pet_friendly');
  }, [filtersFromQuery, isCampgrounds]);

  const getDriverAgeDisplayText = useCallback(() => {
    const driverAge = filtersFromQuery[ESearchFilters.FILTER_RENTER_AGE];
    if (!driverAge) {
      return '';
    }
    const ageValue = Number(driverAge);
    return ageValue === 21 ? '(21-24)' : '(25+)';
  }, [filtersFromQuery]);

  const getMoreCountFromQuery = useCallback(() => {
    // Count primary filters that are selected (only when moveFiltersFlagEnabled is true)
    const scorePrimaryFilters = [
      // RV Type
      getRVTypeCountFromQuery(),
      // RV Profile
      getRVProfileCountFromQuery(),
      // Price
      getPriceCountFromQuery(),
      // Pet Friendly
      getPetIsActiveFromQuery() ? 1 : 0,
      // Instant Book
      instantBookFromQuery === 'true' ? 1 : 0,
      // Delivery
      deliveryStationaryFromQuery === 'true' ? 1 : 0,
      // Superhost (if enabled and not moved)
      /superhost/i.test(getParamAsString(filterTagsSlugFromQuery) || '') ? 1 : 0,
      // Pro (if enabled and not moved)
      filterOwnerTypeFromQuery === 'dealer' ? 1 : 0,
    ].reduce((sum, count) => sum + count, 0);

    // Count secondary filters
    // Amenities filters score
    const scoreAmenities =
      amenitiesFromFilterFeatureArray(
        getParamAsString(filtersFromQuery[ESearchFilters.FILTER_FEATURE])?.split(',') || [],
        amenitiesItems,
      ).length || 0;

    // More filters score
    const scoreCancelPolicy =
      getParamAsString(filtersFromQuery[ESearchFilters.CANCEL_POLICY])?.split(',').length || 0;
    const scoreKeywords = filtersFromQuery[ESearchFilters.FILTER_KEYWORDS] ? 1 : 0;

    // Check if length filter is at default values
    const lengthBetween =
      getParamAsString(filtersFromQuery[ESearchFilters.LENGTH_BETWEEN])?.split(',') || [];
    const scoreLength = lengthBetween.length > 0 && lengthBetween[0] !== '0-255' ? 1 : 0;

    const scoreRules =
      moreFromFilterFeatureArray(
        getParamAsString(filtersFromQuery[ESearchFilters.FILTER_FEATURE])
          ?.split(',')
          .filter(rule => rule !== 'pet_friendly') || [],
        'rv',
      ).length || 0;
    const scoreStarRating = Number(filtersFromQuery[ESearchFilters.SCORE]) > 0 ? 1 : 0;
    const scoreUnlimitedMiles = filtersFromQuery[ESearchFilters.UNLIMITED_MILES] === 'true' ? 1 : 0;
    const scoreUnlimitedGenerator =
      filtersFromQuery[ESearchFilters.UNLIMITED_GENERATOR] === 'true' ? 1 : 0;
    const scoreWeightGreater = Number(filtersFromQuery[ESearchFilters.WEIGHT_GREATER]) > 0 ? 1 : 0;
    const scoreWeightLesser = Number(filtersFromQuery[ESearchFilters.WEIGHT_LESSER]) ? 1 : 0;
    const scoreSeatbelts = Number(filtersFromQuery[ESearchFilters.SEATBELTS]) ? 1 : 0;

    // Make filters score
    const scoreMake = filtersFromQuery[ESearchFilters.FILTER_VEHICLE_MAKE] ? 1 : 0;
    const scoreModels = filtersFromQuery[ESearchFilters.FILTER_VEHICLE_MAKE_MODELS] ? 1 : 0;
    const scoreMakeYearRange =
      filtersFromQuery[ESearchFilters.YEAR_GREATER] || filtersFromQuery[ESearchFilters.YEAR_LESSER]
        ? 1
        : 0;

    const scoreVehicleAmps = filtersFromQuery[ESearchFilters.FILTER_VEHICLE_AMPS] ? 1 : 0;

    const secondaryFiltersCount =
      scoreAmenities +
      scoreCancelPolicy +
      scoreKeywords +
      scoreLength +
      scoreRules +
      scoreStarRating +
      scoreUnlimitedGenerator +
      scoreUnlimitedMiles +
      scoreWeightGreater +
      scoreWeightLesser +
      scoreMake +
      scoreModels +
      scoreMakeYearRange +
      scoreSeatbelts +
      scoreVehicleAmps;

    return scorePrimaryFilters + secondaryFiltersCount;
  }, [
    amenitiesItems,
    filtersFromQuery,
    filterTagsSlugFromQuery,
    filterOwnerTypeFromQuery,
    getRVTypeCountFromQuery,
    getRVProfileCountFromQuery,
    getPriceCountFromQuery,
    getPetIsActiveFromQuery,
    instantBookFromQuery,
    deliveryStationaryFromQuery,
  ]);

  const handleModalDismiss = useCallback(() => {
    setFilterVehiclesShow(1);
    dispatch(setSelectedFilter(null));
    modalTriggerState.close();
  }, [dispatch, modalTriggerState]);

  const handleDropdownDismiss = useCallback(() => {
    setFilterVehiclesShow(1);
    dropdownTriggerState.close();
  }, [dropdownTriggerState]);

  const handleOpenModalFilter = useCallback(
    (filter: EFilterCategories) => {
      setFilterVehiclesShow(1);
      dispatch(setSelectedFilter(filter));
      if (isModalOpen) {
        modalTriggerState.close();
      } else {
        modalTriggerState.open();
      }
    },
    [dispatch, isModalOpen, modalTriggerState],
  );

  const handleOpenDropdownFilter = useCallback(
    (filter: EFilterCategories) => {
      setFilterVehiclesShow(1);
      dispatch(setSelectedFilter(filter));
      if (isDropdownOpen) {
        dropdownTriggerState.close();
      } else {
        dropdownTriggerState.open();
      }
    },
    [dispatch, dropdownTriggerState, isDropdownOpen],
  );

  // Handle filters that act as toggles
  const handleToggleFilter = useCallback(
    (filter: EFilterCategories) => {
      setFilterVehiclesShow(1);

      const filterToUpdate: Record<string, string | string[] | undefined> = {};
      const currentFeatures = featuresFromQuery?.toString().split(',') || [];

      switch (filter) {
        case EFilterCategories.SUPERHOST:
          filterToUpdate[ESearchFilters.FILTER_TAGS_SLUG] = xor(
            getParamAsString(filterTagsSlugFromQuery)
              ?.split(',')
              .map(tag => tag.trim()) || [],
            ['superhost'],
          ).join(',');
          break;
        case EFilterCategories.INSTANT_BOOK: {
          const newInstantBookValue = instantBookFromQuery !== 'true';
          filterToUpdate[ESearchFilters.INSTANT_BOOK] = String(newInstantBookValue);
          // Update form filter to keep it in sync
          dispatch(
            setFormFilter(
              {
                filters: { [ESearchFilters.INSTANT_BOOK]: newInstantBookValue },
                filterMethod: EFilterMethod.CHECKBOX,
              },
              false,
            ),
          );
          break;
        }
        case EFilterCategories.PET_FRIENDLY:
          if (currentFeatures?.includes('pet_friendly')) {
            const newFeatures = currentFeatures.filter(el => el !== 'pet_friendly').join(',');
            filterToUpdate[ESearchFilters.FILTER_FEATURE] = newFeatures;
          } else {
            currentFeatures.push('pet_friendly');
            filterToUpdate[ESearchFilters.FILTER_FEATURE] = String(currentFeatures.join(','));
          }
          break;
        case EFilterCategories.PRO:
          filterToUpdate[ESearchFilters.FILTER_OWNERTYPE] =
            filterOwnerTypeFromQuery === 'dealer' ? undefined : 'dealer';
          break;
      }

      if (!Object.keys(filterToUpdate).length) {
        return;
      }

      // Include initialParams when setting form filter
      dispatch(
        setFormFilter(
          {
            filters: { ...filterToUpdate },
            filterMethod: EFilterMethod.CHECKBOX,
          },
          false,
        ),
      );
      dispatch(applySearchFilter({ ...filterToUpdate }, false, false, false, undefined, false));
    },
    [
      dispatch,
      featuresFromQuery,
      filterOwnerTypeFromQuery,
      filterTagsSlugFromQuery,
      instantBookFromQuery,
    ],
  );

  const handleToggleCampgroundFilter = useCallback(
    (filter: EFilterCategories) => {
      setFilterVehiclesShow(1);

      const filterToUpdate: Record<string, string | string[] | undefined> = {};
      const currentFeatures = campgroundFeaturesFromQuery?.toString().split(',') || [];

      switch (filter) {
        case EFilterCategories.PET_FRIENDLY:
          if (currentFeatures?.includes('allows_pets')) {
            const newFeatures = currentFeatures.filter(el => el !== 'allows_pets').join(',');
            filterToUpdate[ESearchFilters.CAMPGROUND_FEATURES] = newFeatures;
          } else {
            currentFeatures.push('allows_pets');
            filterToUpdate[ESearchFilters.CAMPGROUND_FEATURES] = String(currentFeatures.join(','));
          }
          break;
      }

      if (!Object.keys(filterToUpdate).length) {
        return;
      }

      // Include initialParams when setting form filter
      dispatch(
        setFormFilter(
          {
            filters: { ...filterToUpdate },
            filterMethod: EFilterMethod.CHECKBOX,
          },
          false,
        ),
      );
      dispatch(applySearchFilter({ ...filterToUpdate }, false, false, false, undefined, false));
    },
    [dispatch, campgroundFeaturesFromQuery],
  );

  // Create the "Bring it to me" filter for rental mode
  const bringItToMeFilter = useBringItToMeFilter(
    () => handleOpenModalFilter(EFilterCategories.DELIVERY),
    isDropdownOpen && selectedFilter === EFilterCategories.DELIVERY,
  );

  const items = useMemo(() => {
    // Determine which delivery filter to show
    const getDeliveryFilter = () => {
      if (isRentalsVDeliverySearchModeEnabled && isDeliveryMode) {
        // Hide delivery filter in delivery mode when flag is enabled
        return null;
      } else if (isRentalsVDeliverySearchModeEnabled && !isDeliveryMode) {
        // Show "Bring it to me" filter in rental mode when flag is enabled
        return bringItToMeFilter;
      } else {
        // Show regular delivery filter when flag is disabled
        return {
          variation: EFilterCategories.DELIVERY,
          active: !!deliveryStationaryFromQuery,
          count: 0,
          onClick: () => handleOpenModalFilter(EFilterCategories.DELIVERY),
          selected: isDropdownOpen && selectedFilter === EFilterCategories.DELIVERY,
          title: intl.formatMessage({
            defaultMessage: 'Delivery',
            id: 'QQ2w0O',
            description: 'Filter menu: Get it delivered',
          }),
        };
      }
    };

    const deliveryFilter = getDeliveryFilter();
    const baseItems = [
      ...(deliveryFilter ? [deliveryFilter] : []),
      {
        variation: EFilterCategories.TYPE,
        count: getRVTypeCountFromQuery(),
        onClick: () => handleOpenModalFilter(EFilterCategories.TYPE),
        selected: isModalOpen && selectedFilter === EFilterCategories.TYPE,
        title: intl.formatMessage({
          defaultMessage: 'RV type',
          id: 'c/zbfh',
          description: 'Screen size large, filter menu: RV type',
        }),
      },
      {
        variation: EFilterCategories.RV_PROFILE,
        count: getRVProfileCountFromQuery(),
        onClick: () => handleOpenModalFilter(EFilterCategories.RV_PROFILE),
        selected: isModalOpen && selectedFilter === EFilterCategories.RV_PROFILE,
        title: intl.formatMessage({
          defaultMessage: 'RV profile',
          id: 'fnMGP9',
          description: 'Screen size large, filter menu: RV profile',
        }),
      },
      {
        variation: EFilterCategories.PRICE,
        count: getPriceCountFromQuery(),
        onClick: isMobile
          ? () => handleOpenModalFilter(EFilterCategories.PRICE)
          : () => handleOpenDropdownFilter(EFilterCategories.PRICE),
        selected: isDropdownOpen && selectedFilter === EFilterCategories.PRICE,
        title: intl.formatMessage({
          defaultMessage: 'Price',
          id: 'PtusVU',
          description: 'Screen size large, filter menu: Price',
        }),
      },
      ...(!selectedCampground
        ? [
            {
              variation: EFilterCategories.INSTANT_BOOK,
              onClick: () => handleToggleFilter(EFilterCategories.INSTANT_BOOK),
              active: instantBookFromQuery === 'true',
              title: (
                <>
                  <GeneralInstantBookIcon className="mr-1 -ml-1 text-lg text-canvas-300" />
                  {intl.formatMessage({
                    defaultMessage: 'Instant book',
                    id: 'IM85Qt',
                    description: 'Screen size large, filter menu: Instant book',
                  })}
                </>
              ),
              customIcon: <></>,
            },
          ]
        : []),

      {
        variation: EFilterCategories.PET_FRIENDLY,
        onClick: () =>
          isCampgrounds
            ? handleToggleCampgroundFilter(EFilterCategories.PET_FRIENDLY)
            : handleToggleFilter(EFilterCategories.PET_FRIENDLY),
        active: getPetIsActiveFromQuery(),
        title: intl.formatMessage({
          defaultMessage: 'Pet friendly',
          id: 'JLo45i',
          description: 'Screen size large, filter menu: Pet Friendly',
        }),
        customIcon: <></>,
      },
      {
        variation: EFilterCategories.DRIVER_AGE,
        onClick: () => handleOpenModalFilter(EFilterCategories.DRIVER_AGE),
        selected: isModalOpen && selectedFilter === EFilterCategories.DRIVER_AGE,
        active: !!filtersFromQuery[ESearchFilters.FILTER_RENTER_AGE],
        title: filtersFromQuery[ESearchFilters.FILTER_RENTER_AGE]
          ? intl.formatMessage(
              {
                defaultMessage: "Driver's age {age}",
                id: 'qEAlP+',
                description: 'Screen size large, filter menu: Driver age selected',
              },
              { age: getDriverAgeDisplayText() },
            )
          : intl.formatMessage({
              defaultMessage: "Driver's age",
              id: 'r4gi1K',
              description: 'Screen size large, filter menu: Driver age unselected',
            }),
      },
      {
        variation: EFilterCategories.MORE,
        count: getMoreCountFromQuery(),
        onClick: () => handleOpenModalFilter(EFilterCategories.MORE),
        selected: isModalOpen && selectedFilter === EFilterCategories.MORE,
        title: (
          <>
            <GeneralFilterIcon className="w-4 h-4 mr-1 -ml-1" />
            {intl.formatMessage({
              defaultMessage: 'All filters',
              id: '0eT8eo',
              description: 'Screen size large, filter menu: More',
            })}
          </>
        ),
      },
    ]
      .filter(item => {
        // Based on the incoming promotional filter conditions, hide certain items from the filter.
        return !hideFilter(item.variation);
      })
      .sort((a, b) => {
        if (a.variation === EFilterCategories.MORE) return 1;

        const aIsSelected = a.active || (a.count ?? 0) > 0;
        const bIsSelected = b.active || (b.count ?? 0) > 0;

        if (aIsSelected && !bIsSelected) return -1;
        if (!aIsSelected && bIsSelected) return 1;
        return 0;
      });

    return baseItems;
  }, [
    selectedCampground,
    intl,
    deliveryStationaryFromQuery,
    isDropdownOpen,
    isCampgrounds,
    selectedFilter,
    getRVTypeCountFromQuery,
    isModalOpen,
    getPriceCountFromQuery,
    isMobile,
    instantBookFromQuery,
    getPetIsActiveFromQuery,
    getMoreCountFromQuery,
    handleToggleFilter,
    handleToggleCampgroundFilter,
    handleOpenModalFilter,
    handleOpenDropdownFilter,
    hideFilter,
    bringItToMeFilter,
    isDeliveryMode,
    isRentalsVDeliverySearchModeEnabled,
    filtersFromQuery,
    getDriverAgeDisplayText,
    getRVProfileCountFromQuery,
  ]);

  useEffect(() => {
    // Don't trigger the modal if:
    // 1. The Optimizely flag hasn't loaded yet (prevent premature triggering)
    // 2. We're in delivery mode and the experiment flag is enabled (delivery filter is hidden)
    const shouldTriggerFromStationaryFilter =
      isStationaryFilterTriggered && !(isRentalsVDeliverySearchModeEnabled && isDeliveryMode);

    if (shouldTriggerFromStationaryFilter) {
      handleOpenModalFilter(EFilterCategories.DELIVERY);
    }
  }, [
    isStationaryFilterTriggered,
    isRentalsVDeliverySearchModeEnabled,
    isDeliveryMode,
    handleOpenModalFilter,
  ]);

  useEffect(() => {
    const { openStationaryDeliveryModal, ...restQuery } = router.query;

    // Don't trigger the modal if:
    // 1. The Optimizely flag hasn't loaded yet (prevent premature triggering)
    // 2. We're in delivery mode and the experiment flag is enabled (delivery filter is hidden)
    const shouldTriggerModal =
      openStationaryDeliveryModal === 'true' &&
      !isModalOpen &&
      !(isRentalsVDeliverySearchModeEnabled && isDeliveryMode);

    if (shouldTriggerModal) {
      handleOpenModalFilter(EFilterCategories.DELIVERY);
    }

    // clean up stationary modal trigger param
    if (openStationaryDeliveryModal) {
      router.replace({ query: restQuery }, undefined, { shallow: true });
    }
  }, [
    router,
    isModalOpen,
    isRentalsVDeliverySearchModeEnabled,
    isDeliveryMode,
    handleOpenModalFilter,
  ]);

  return (
    <>
      {/* Renders menu pills on desktop and mweb */}
      <FilterPills items={items} setFieldElement={setFieldElement} />

      {/* Handles filter content */}
      <FiltersPillsContentContainer
        isDropdownOpen={isDropdownOpen}
        isModalOpen={isModalOpen}
        dropdownTriggerState={dropdownTriggerState}
        triggerRef={triggerRef}
        handleDropdownDismiss={handleDropdownDismiss}
        handleModalDismiss={handleModalDismiss}
      />
    </>
  );
};

export default FilterPillsContainer;
